export const SERVICES = [
  {
    slug: "ahu-maintenance",
    title: "AHU Maintenance",
    overline: "01",
    short: "Filter changes, motor checks and full AHU maintenance to keep systems efficient, compliant and disruption-free.",
    intro:
      "Air Handling Units keep your building's ventilation running smoothly. Our maintenance covers everything from filter changes to motor checks, ensuring your AHU operates efficiently and meets compliance requirements without disruption to your operations.",
    body: [
      "AHUs work hard to maintain air quality and temperature control across your premises. Regular maintenance prevents breakdowns and keeps energy costs down. Our engineers clean coils, replace filters, check motors and belts, and test all controls to British Standards including BS EN 16798-1 and SFG20 guidelines.",
      "We spot potential issues before they become problems, minimising downtime and ensuring your system runs at peak efficiency. Whether it's routine servicing or emergency repairs, we handle chamber cleaning, gasket replacements, and drain modifications to keep your air handling running reliably year-round.",
    ],
    coverageHeading: "What we cover",
    coverage: [
      "Filter replacement and management (BS EN 1822, ISO 16890)",
      "Planned preventative maintenance programmes",
      "Motor monitoring, greasing and replacement",
      "Coil cleaning, flushing and repairs",
    ],
    deepDive:
      "Our service includes comprehensive chamber cleaning and full inspection of belts, pulleys, and actuators. We handle differential pressure checks, drain cleaning and modifications, plus gasket replacements to prevent air leakage. All work follows SFG20 64-10 and 64-20 standards, with heat exchanger cleaning and drying wheel maintenance included. We'll upgrade lighting, fabricate repairs, carry out rust proofing, and ensure fire damper compliance to BS 9999.",
    image:
      "/hero/engineer-work.jpg",
    span: "lg:col-span-7",
  },
  {
    slug: "ac-refrigeration",
    title: "A/C & Refrigeration",
    overline: "02",
    short: "Reliable AC and refrigeration, F-Gas compliance, and legal requirements with no paperwork hassle.",
    intro:
      "Air conditioning and refrigeration systems need regular maintenance to stay efficient and compliant. Our service keeps your equipment running reliably, manages F-Gas regulations, and ensures you meet all legal requirements without the paperwork headaches.",
    body: [
      "Proper maintenance of cooling systems isn't just about performance — it's about compliance too. We handle everything from routine servicing to F-Gas leak testing and record keeping. Our engineers are Refcom certified and follow BS EN 378 and BS ISO 5149 standards.",
      "We maintain comprehensive log books covering leak tests, refrigerant bottle movements, equipment testing, and engineer records. Regular servicing reduces energy costs, prevents breakdowns, and keeps your systems compliant with F-Gas Regulations and PUWER requirements.",
    ],
    coverageHeading: "Our service approach",
    coverage: [
      "Refrigerant phase-out planning and sustainability advice",
      "Complete log book and certification management",
      "Planned maintenance and emergency repairs",
      "F-Gas compliance and leak testing",
    ],
    deepDive:
      "Our comprehensive service includes cleaning, servicing and certification to manufacturer specifications and British Standards. We conduct regular leak tests, manage refrigerant records, and maintain all required documentation. Fire containment checks ensure your systems meet fire strategy requirements. All our engineers hold current F-Gas qualifications and follow HSG297 safety guidelines.",
    image:
      "/hero/engineer-onsite.jpg",
    span: "lg:col-span-5",
    callouts: [
      { title: "Leak checks are a legal duty", body: "Systems holding 5 tonnes CO₂e or more must be leak-checked: every 12 months (5–50 t), every 6 months (50–500 t), every 3 months (500 t+). Automatic leak detection halves the frequency." },
      { title: "R22 can't be topped up", body: "Using HCFC R22 to service equipment has been banned since 2015. If your system still runs on it, we'll plan a replacement or a compliant retrofit." },
      { title: "Keep records for 5 years", body: "The F-Gas log book (leak tests, refrigerant added or recovered, engineer details) must be kept for at least five years. We keep yours up to date after every visit." },
      { title: "TM44 for systems over 12 kW", body: "Air conditioning with a combined output above 12 kW needs a TM44 energy inspection at least every five years. We can carry it out on the same visit." },
    ],
    checklist: [
      "Where the units are and how we get to them (roof, plant room, keys, ladder access)",
      "Make, model and serial numbers, or your asset list if you have one",
      "Your F-Gas log book or the last leak-test certificate",
      "Refrigerant type and charge, if you know it (it's on the unit's data plate)",
      "What's going wrong: symptoms, error codes, and photos if you can",
      "Site induction, permit-to-work or RAMS requirements",
      "Parking, loading and any out-of-hours or noise restrictions",
    ],
    serviceAreas: [
      { region: "West London (our base, UB7)", places: "Hillingdon, Ealing, Hounslow, Hammersmith & Fulham, Brent, Harrow, Richmond" },
      { region: "Central London", places: "Westminster, Kensington & Chelsea, Camden, the City" },
      { region: "Home Counties", places: "Berkshire (Slough, Windsor, Reading) and Surrey (Staines, Woking)" },
      { region: "London & the M25 corridor", places: "All of Greater London and towns along the M25. Further afield? Ask us, we take jobs case by case." },
      { region: "Emergency call-outs", places: "24/7 across London and the M25 corridor" },
    ],
  },
  {
    slug: "kitchen-extract",
    title: "Kitchen Extract Maintenance",
    overline: "03",
    short: "Remove grease, reduce fire risk and stay TR19 compliant — with the kitchen running.",
    intro:
      "Kitchen extract systems remove grease, smoke and heat from commercial kitchens. Regular maintenance keeps them working efficiently, reduces fire risk, and ensures you meet health and safety requirements. We handle the cleaning and compliance so you can focus on service.",
    body: [
      "Grease build-up in extract systems is a serious fire hazard and health risk. Our maintenance service thoroughly cleans ductwork, canopies and fans to remove grease deposits and maintain proper airflow. We follow HSG103 guidance and BS EN 15780 standards, conducting dust thickness testing and TR19 inspections to verify cleanliness.",
      "Regular servicing prevents costly breakdowns during peak service hours and keeps insurance requirements met. Our engineers work outside kitchen operating hours where possible, minimising disruption to your business.",
    ],
    coverageHeading: "Complete extract system care",
    coverage: [
      "Filter replacement and maintenance schedules",
      "Deep cleaning of ductwork, canopies and fans",
      "TR19 compliance and dust thickness testing",
      "Fire safety certification and documentation",
    ],
    deepDive:
      "We clean extract systems to TR19 standards, removing grease accumulation from all accessible surfaces including hard-to-reach areas. Our service includes motor maintenance, belt checks, and fan balancing. We test and certify fire dampers to BS 9999, ensuring fire containment systems work when needed. All cleaning follows SFG20 65-10 and 65-20 guidelines, with full LEV testing where required.",
    image:
      "/hero/kitchen-extract.jpg",
    span: "lg:col-span-6",
  },
  {
    slug: "steam-trap-psv",
    title: "Steam Trap & PSV Maintenance",
    overline: "04",
    short: "Faulty traps waste energy. Failed PSVs create hazards. We keep steam systems efficient and compliant.",
    intro:
      "Steam systems need proper maintenance to run safely and efficiently. Faulty steam traps waste energy and money, while failed safety valves create serious hazards. Our service keeps your steam equipment working correctly, reducing costs and meeting all legal safety requirements.",
    body: [
      "Steam traps that fail open waste expensive energy by allowing live steam to pass through. Failed closed traps cause waterlogging, reducing heat transfer and damaging equipment. We test and maintain steam traps to BS 759 standards, identifying failures and optimising your system's performance.",
      "Safety valve testing follows BS 806 and PSSR requirements, ensuring pressure relief devices work when needed. Our engineers are trained to CIBSE Guide M specifications and follow HSG261 safety protocols.",
    ],
    coverageHeading: "Comprehensive steam system care",
    coverage: [
      "Energy efficiency surveys and improvement advice",
      "Steam trap testing and replacement programmes",
      "Full documentation for insurance and compliance",
      "Safety valve inspection and certification to PSSR",
    ],
    deepDive:
      "We conduct thorough testing of all steam traps using ultrasonic and temperature methods, identifying failures and calculating energy losses. Failed traps are repaired or replaced promptly. Safety valves are inspected, tested and certified to current regulations, with written schemes of examination maintained. Our service follows SFG20 71-10 and 71-20 standards.",
    image:
      "/hero/engineer-work.jpg",
    span: "lg:col-span-6",
  },
  {
    slug: "mvhr-ventilation",
    title: "MVHR & Ventilation",
    overline: "05",
    short: "Toilet extract, MVHR, door curtains and air purifiers — kept healthy, efficient and compliant.",
    intro:
      "Mechanical Ventilation with Heat Recovery systems and other ventilation equipment need regular servicing to maintain air quality and energy efficiency. Our maintenance covers MVHR units, toilet extract, door curtains and air purifiers, keeping your indoor environment healthy and compliant.",
    body: [
      "MVHR systems recover heat from extracted air, reducing heating costs whilst maintaining fresh air supply. Without proper maintenance, filters clog, heat exchangers lose efficiency, and indoor air quality suffers. We clean heat recovery units, replace filters, and check fan performance to manufacturer specifications.",
      "We maintain door curtains and air purifiers, cleaning components and replacing filters as needed. All work follows relevant British Standards and Building Regulations requirements.",
    ],
    coverageHeading: "Full ventilation system support",
    coverage: [
      "Filter replacement programmes for all ventilation equipment",
      "MVHR unit servicing and heat exchanger cleaning",
      "Toilet extract and bathroom fan maintenance",
      "Air curtain and air purifier servicing",
    ],
    deepDive:
      "Our comprehensive maintenance includes thorough cleaning of heat recovery cores, filter replacement to ISO 16890 standards, and fan performance testing. We check ductwork connections, clean grilles and diffusers, and verify airflow rates meet design specifications. Door curtains receive motor checks and element cleaning. We follow SFG20 guidelines and Building Regulations Part F.",
    image:
      "/hero/atrium.jpg",
    span: "lg:col-span-6",
  },
  {
    slug: "fire-damper-drop-testing",
    title: "Fire Damper Drop Testing",
    overline: "06",
    short: "Drop testing, repairs and certification to BS 9999 and the Regulatory Reform (Fire Safety) Order 2005.",
    intro:
      "Fire dampers prevent fire and smoke spreading through ventilation systems. Regular drop testing ensures they close properly when needed, meeting legal requirements under the Regulatory Reform (Fire Safety) Order 2005. We test, maintain and certify all your fire dampers efficiently.",
    body: [
      "Fire dampers are critical safety devices that must work in an emergency. Over time, dust, debris and corrosion can prevent dampers closing fully, compromising fire compartmentation. Our engineers test each damper by triggering the release mechanism and verifying it closes completely within the correct timeframe.",
      "We follow BS 9999 and DW145 standards, cleaning mechanisms, freeing seized components, and replacing faulty fusible links. All dampers are reset and tested again to confirm operation.",
    ],
    coverageHeading: "Complete fire damper compliance",
    coverage: [
      "Damper cleaning, freeing and fusible link replacement",
      "Drop testing to BS 9999 and RRFSO requirements",
      "Detailed location schedules and asset registers",
      "Full certification for fire safety compliance",
    ],
    deepDive:
      "We conduct comprehensive surveys to locate all fire dampers, creating accurate asset registers with grid references and photographic records. Each damper is accessed, tested, and any faults recorded. Seized dampers are freed, actuators cleaned, and worn components replaced. We test motorised dampers electrically and verify integration with fire alarm systems. Our reports satisfy building control, insurance surveyors and fire risk assessors.",
    image:
      "/hero/engineer-onsite.jpg",
    span: "lg:col-span-6",
  },
];

export const findService = (slug) => SERVICES.find((s) => s.slug === slug);
